import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { MapPin, Navigation, Phone, Mail, Clock, ExternalLink } from 'lucide-react';
import { companyInfo } from '../mock';

export const LocationMap = () => {
  // Coordinates for Mehsana, Gujarat (approximate location)
  const latitude = 23.5880;
  const longitude = 72.3693;
  
  const handleDirections = () => {
    const address = encodeURIComponent(companyInfo.address);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}`, '_blank');
  };

  const handleViewOnMap = () => {
    const address = encodeURIComponent(companyInfo.address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
  };

  return (
    <section id="location" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-green-100 px-6 py-3 rounded-full mb-6">
            <MapPin className="h-5 w-5 text-green-600" />
            <span className="text-green-700 font-semibold">Visit Our Training Center</span>
          </div>
          <h2 className="text-5xl font-black text-gray-900 mb-6 leading-tight">
            Find Us in <br />
            <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
              Mehsana, Gujarat
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Located in the heart of Mehsana, our state-of-the-art training facility is easily accessible 
            and equipped with modern amenities for the best learning experience.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12">
          {/* Map Section */}
          <div className="lg:col-span-8">
            <Card className="border-0 shadow-2xl overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-green-500 to-blue-500"></div>
              <CardHeader className="bg-gradient-to-br from-green-50 to-blue-50 p-6">
                <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                  <MapPin className="h-6 w-6 mr-3 text-green-600" />
                  Interactive Location Map
                </CardTitle>
                <p className="text-gray-600">Click on the map or use the buttons below to get directions</p>
              </CardHeader>
              <CardContent className="p-0">
                <div className="relative h-96 bg-gray-100">
                  {/* Embedded Google Map */}
                  <iframe
                    src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3693.0!2d${longitude}!3d${latitude}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjPCsDM1JzE2LjgiTiA3MsKwMjInMDkuNSJF!5e0!3m2!1sen!2sin!4v1697123456789!5m2!1sen!2sin&q=${encodeURIComponent(companyInfo.address)}`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="rounded-b-lg"
                  ></iframe>
                </div>
                
                {/* Map Action Buttons */}
                <div className="p-6 bg-white border-t">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button 
                      onClick={handleDirections}
                      className="flex-1 bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white font-bold py-3 rounded-xl"
                    >
                      <Navigation className="h-5 w-5 mr-2" />
                      Get Directions
                      <ExternalLink className="h-4 w-4 ml-2" />
                    </Button>
                    <Button 
                      onClick={handleViewOnMap}
                      variant="outline"
                      className="flex-1 border-2 border-green-300 text-green-700 hover:bg-green-50 font-bold py-3 rounded-xl"
                    >
                      <MapPin className="h-5 w-5 mr-2" />
                      View on Google Maps
                      <ExternalLink className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Location Details */}
          <div className="lg:col-span-4 space-y-8">
            {/* Address Card */}
            <Card className="border-0 shadow-xl">
              <div className="h-2 bg-gradient-to-r from-orange-500 to-yellow-500"></div>
              <CardHeader className="bg-gradient-to-br from-orange-50 to-yellow-50 p-6">
                <CardTitle className="text-xl font-bold text-gray-900">Training Center Address</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Complete Address</p>
                      <p className="text-gray-700 leading-relaxed">{companyInfo.address}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="border-0 shadow-xl">
              <div className="h-2 bg-gradient-to-r from-blue-500 to-purple-500"></div>
              <CardHeader className="bg-gradient-to-br from-blue-50 to-purple-50 p-6">
                <CardTitle className="text-xl font-bold text-gray-900">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center">
                      <Phone className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Phone</p>
                      <a 
                        href={`tel:${companyInfo.phone}`} 
                        className="text-green-600 hover:text-green-700 font-medium text-lg transition-colors"
                      >
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                      <Mail className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Email</p>
                      <a 
                        href={`mailto:${companyInfo.email}`} 
                        className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
                      >
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                      <Clock className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Training Hours</p>
                      <p className="text-gray-700">Monday - Saturday: 9:00 AM - 6:00 PM</p>
                      <p className="text-gray-700">Sunday: By Appointment</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Nearby Landmarks */}
            <Card className="border-0 shadow-xl">
              <div className="h-2 bg-gradient-to-r from-purple-500 to-pink-500"></div>
              <CardHeader className="bg-gradient-to-br from-purple-50 to-pink-50 p-6">
                <CardTitle className="text-xl font-bold text-gray-900">Nearby Landmarks</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <span className="text-gray-700">Opposite SFW Gym</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
                    <span className="text-gray-700">Radhanpur Road</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <span className="text-gray-700">Saundarya Silver Complex</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
                    <span className="text-gray-700">Mehsana 384002</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-3xl p-8 max-w-2xl mx-auto">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Ready to Visit Our Training Center?
            </h3>
            <p className="text-gray-600 mb-6 text-lg">
              Schedule a visit to our facility or contact us to learn more about our drilling safety training programs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white px-8 py-3 rounded-xl font-bold text-lg"
              >
                <MapPin className="h-5 w-5 mr-2" />
                Schedule a Visit
              </Button>
              <Button 
                onClick={() => document.getElementById('courses').scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-green-300 text-green-700 hover:bg-green-50 px-8 py-3 rounded-xl font-bold text-lg"
              >
                View Training Programs
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};